import '@mdi/font/css/materialdesignicons.min.css';
import 'vuetify/dist/vuetify.min.css';

import { Options as DialogOptions } from '@/components/mi-dialog/MiDialog';
import MaiaChat from '@/components/MaiaChat/MaiaChat';
import Navigation from '@/components/navigation/Navigation';
import { User } from '@/models/User';
import { Breadcrumb } from '@/support/Breadcrumb';
import { Token } from '@atabix/core';
import axios, { AxiosResponse } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { CustomBreakpoint, ScreenInfo } from './store';
import { loadLanguageAsync } from './i18n';

@Component<App>({
  components: {
    MaiaChat,
    Navigation,
  },
  beforeRouteUpdate(to, from, next) {
    next();
  },
})
export default class App extends Vue {
  //#region Props

  public breadcrumbs: Breadcrumb[] = [];

  protected customBreakpoints: CustomBreakpoint[] = [1225, 1350, 1450, 1500, 1550, 1567];

  /**
   * Open state of the Maia panel.
   *
   * It lives here, not in the webshop view, so that opening a product in the
   * main window does not close the conversation the stylist is having about it.
   */
  protected isShowingMaiaChat: boolean = false;

  //#endregion

  //#region Lifecycle hooks

  protected created(): void {
    this.onCreated();
  }

  public mounted(): void {
    this.onMounted();
  }

  protected beforeDestroy(): void {
    this.onBeforeDestroy();
  }

  //#endregion

  //#region Lifecycle hook handlers

  protected onCreated(): void {
    this.initializeOnResize();
  }

  protected onMounted(): void {
    this.getCurrentIpCountry();
    if (Token.get('access')) {
      new User()
        .current()
        .then((user: User) => {
          this.$store.dispatch('userAuthenticated', user);
          loadLanguageAsync(user.language || 'en')
        })
        .catch(() => {
          Token.delete('access');
        });
    }
  }

  protected onBeforeDestroy(): void {
    window.removeEventListener("resize", this.onResize);
  }

  //#endregion

  //#region Lifecycle hook helpers

  /**
   * Initialize is set as a callback to the initialize event by App.mounted
   *
   * @memberof App
   */


  protected initializeOnResize(): void {
    window.addEventListener("resize", this.onResize);
    this.onResize();
  }

  protected deinitializeOnResize(): void {

  }

  //#endregion

  //#region Handlers

  protected onResize() {
    this.resize();
  }

  //#endregion

  //#region Helpers

  protected toggleMenuState(type: string, forcedState: boolean) {
    if (type === 'mini') {
      this.$store.dispatch('toggleMini', forcedState);
    } else {
      this.$store.dispatch('toggleDrawer', forcedState);
    }
  }

  protected getCurrentIpCountry() {
    axios.get('https://ip2c.org/s')
    .then((response: AxiosResponse) => {
      if (response.data) {
        const locale = response.data.split(';')[1];
        this.$store.dispatch('setCurrentIpCountry', locale);
      }
    })
    .catch(() => {});
  }

  protected resize(): void {
    const screenSmallerThan: CustomBreakpoint[] = this.customBreakpoints
      .map((breakpoint: CustomBreakpoint | 0) => window.innerWidth < breakpoint ? breakpoint : 0)
      .filter((breakpoint: CustomBreakpoint | 0) => breakpoint !== 0) as CustomBreakpoint[];

    const screenEqualsOrBiggerThan: CustomBreakpoint[] = this.customBreakpoints
      .map((breakpoint: CustomBreakpoint | 0) => window.innerWidth >= breakpoint ? breakpoint : 0)
      .filter((breakpoint: CustomBreakpoint | 0) => breakpoint !== 0) as CustomBreakpoint[];

    const screenInfo: ScreenInfo = {
      screenSmallerThan,
      screenEqualsOrBiggerThan,
    };

    this.$store.dispatch('setScreenInfo', screenInfo);

    if (window.innerWidth < 1567) {
      this.toggleMenuState('mini', true);
    }
  }

  //#endregion

  //#region Getters

  /**
   * Maia is for the colleagues on the pilot.
   *
   * The rule itself lives on the backend — administrators plus two named
   * colleagues, see App\Services\Maia\MaiaAccess — and arrives as a flag on
   * the current user. Deliberately not re-derived from `roles` here: the same
   * rule decides whether `maia:chat` answers at all, and a rule written twice
   * is a rule that drifts. Absent means no.
   */
  protected get canUseMaia(): boolean {
    const auth = this.$store && this.$store.state ? this.$store.state.Auth : null;

    return !!auth && (auth as User).can_use_maia === true;
  }

  /**
   * Only while she is actually composing a box.
   *
   * The shop and the product page both sit under /webshop/order/:id, so the
   * widget stays mounted across the one navigation that matters — clicking a
   * product card — and does not follow her around the rest of Sara.
   */
  protected get isComposingABox(): boolean {
    const name = this.$route && this.$route.name ? this.$route.name : '';

    return name === 'webshop.order' || name === 'webshop.order.detail';
  }

  protected get maiaCustomerName(): string {
    return this.$store && this.$store.state ? (this.$store.state.maiaCustomerName || '') : '';
  }

  get dialogOptions(): DialogOptions | null {
    return this.$store.state.dialog;
  }

  get isDisplayingDialog(): boolean {
    return this.dialogOptions !== null;
  }

  set isDisplayingDialog(value: boolean) {
    this.$store.dispatch('closeDialog');
  }

  get isLoginPage():boolean {
    // @ts-ignore
    return this.$route.meta.name === 'login';
  }

  protected get isKanban(): boolean {
    return this.$route.name === 'requests'
  }

  protected get breakpointClasses(): string {
    return [
      ...this.$store.getters.screenSmallerThan.map(
        (breakpoint: CustomBreakpoint) => `screen-smaller-than-${breakpoint}`
      ),
      ...this.$store.getters.screenEqualsOrBiggerThan.map(
        (breakpoint: CustomBreakpoint) => `screen-equals-or-bigger-than-${breakpoint}`
      )
    ].join(' ');
  }

  //#endregion

  //#region @Watchers

  @Watch('$route')
  protected routeChanged(to: Route, from: Route) {
    // this.breadcrumbs.push({ name: 'name', path: to.path });
  }

  //#endregion
}
